import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import cls from 'classnames';

import animateHeight from 'src/animations/animateHeight';
import BreakpointsService, { breakpoints } from 'src/explore/services/breakpoints';
import styles from './index.module.sass';

const MoreBtn = ({ active, bgRGB, btnClass, text = 'Read More', handleClick }) => {
  const handleKeyDown = ( event ) => {
    if ( event.key === 'Enter' || event.key === ' ' ) {
      handleClick();
    }
  };

  return (
    <button
      type="button"
      className={cls( styles.btn, 'position-absolute border-0', btnClass, {
        [styles.btnHide]: active,
      })}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      style={{
        backgroundColor: bgRGB ? `rgba( ${bgRGB[0]}, ${bgRGB[1]}, ${bgRGB[2]} )` : false,
      }}
      tabIndex={0}
    >
      <span
        className={styles.gradientEdge}
        style={{
          backgroundImage: bgRGB
            ? `linear-gradient( 90deg, rgba( ${bgRGB[0]}, ${bgRGB[1]}, ${bgRGB[2]}, 0 ) 0%, rgba( ${bgRGB[0]}, ${bgRGB[1]}, ${bgRGB[2]}, 1 ) 90%, rgba( ${bgRGB[0]}, ${bgRGB[1]}, ${bgRGB[2]}, 1 ) 100% )`
            : false,
        }}
      />
      {text}
    </button>
  );
};

const CollapsibleContainer = ({
  bgRGB = undefined,
  btnClass = undefined,
  children,
  className = '',
  collapseHeight,
  moreText = 'Read More',
  onViewMore = undefined,
}) => {
  const [ active, setActive ] = useState( false );
  const [ collapseTo, setCollapseTo ] = useState( null );
  const [ open, setOpen ] = useState( false );
  const [ overflow, setOverflow ] = useState( false );

  const containerRef = useRef( null );
  const contentRef = useRef( null );

  // get breakpoints from smallest to largest
  const breakpointWidths = useMemo(() => {
    const widths = {};
    Object.keys( breakpoints ).forEach(( size ) => {
      widths[breakpoints[size]] = size;
    });
    return widths;
  }, []);
  // set collapse heights for all breakpoints, in pixels
  const collapseHeightPx = useMemo(() => {
    let lastHeight = null;
    const heightPx = {};
    Object.keys( breakpointWidths ).forEach(( size ) => {
      // null = do not collapse
      if ( collapseHeight[breakpointWidths[size]] === null ) {
        lastHeight = null;
      }
      // if collapse height is set, use it + update lastHeight for reference at next breakpoint
      else if ( collapseHeight[breakpointWidths[size]]) {
        // convert to px if rem
        lastHeight =
          String( collapseHeight[breakpointWidths[size]]).indexOf( 'rem' ) > -1
            ? parseFloat( collapseHeight[breakpointWidths[size]]) * 16
            : collapseHeight[breakpointWidths[size]];
      }
      heightPx[breakpointWidths[size]] = lastHeight;
    });
    return heightPx;
  }, [ breakpointWidths, collapseHeight ]);

  useEffect(() => {
    // determine collapse state
    const content = contentRef;

    const setCollapse = () => {
      // collapse + content height
      setCollapseTo( collapseHeightPx[BreakpointsService.getBreakpointSize()]);
      const contentStyle = getComputedStyle( content.current );
      const contentHeight =
        content.current.offsetHeight +
        parseInt( contentStyle.marginTop, 10 ) +
        parseInt( contentStyle.marginBottom, 10 );

      // collapse set + necessary
      if ( collapseTo && collapseTo < contentHeight ) {
        setOverflow( 'collapse' );
      } else {
        // do not collapse
        setOverflow( false );
      }
    };
    setCollapse();

    window.addEventListener( 'resize', setCollapse );
    return () => {
      window.removeEventListener( 'resize', setCollapse );
    };
  }, [ children, collapseHeightPx, collapseTo ]);

  // initialize setHeight with stable arguments
  const setHeight = useCallback(
    () =>
      animateHeight({
        container: containerRef.current,
        content: contentRef.current,
        duration: 400,
      }),
    []
  );

  const handleClick = () => {
    setActive( true );
    setHeight()({
      open,
      callback: () => {
        setOpen( !open );
      },
      minHeight: collapseTo,
    });

    if ( onViewMore ) onViewMore();
  };

  // set initial collapse height if necessary
  let containerClass = {};
  if ( overflow === 'collapse' && !open ) {
    containerClass = {
      height: collapseTo,
    };
  }

  return (
    <div className="position-relative">
      <div
        className={cls( 'position-relative overflow-hidden', className )}
        style={containerClass}
        ref={containerRef}
      >
        <div className="overflow-hidden" ref={contentRef}>
          {children}
        </div>
        <div
          className={cls( styles.mask, { [styles.maskHide]: !overflow || active })}
          style={{
            top: collapseTo,
            backgroundColor: bgRGB ? `rgba( ${bgRGB[0]}, ${bgRGB[1]}, ${bgRGB[2]} )` : false,
          }}
        />
      </div>
      {overflow === 'collapse' && (
        <MoreBtn
          active={active}
          bgRGB={bgRGB}
          btnClass={btnClass}
          text={moreText}
          handleClick={handleClick}
        />
      )}
    </div>
  );
};

export default CollapsibleContainer;
